import { useState, useCallback } from 'react'
import { ConfirmDialogType } from '@/components/ui/ConfirmDialog'

interface UseConfirmDialogOptions {
    title?: string
    message?: string
    confirmText?: string
    cancelText?: string
    type?: ConfirmDialogType
}

interface ConfirmDialogState {
    isOpen: boolean
    title: string
    message: string
    confirmText: string
    cancelText: string
    type: ConfirmDialogType
    loading: boolean
    onConfirm: (() => void) | (() => Promise<void>)
}

export const useConfirmDialog = () => {
    const [dialogState, setDialogState] = useState<ConfirmDialogState>({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'danger',
        loading: false,
        onConfirm: () => { }
    })

    const showConfirmDialog = useCallback((
        onConfirm: (() => void) | (() => Promise<void>),
        options: UseConfirmDialogOptions = {}
    ) => {
        setDialogState({
            isOpen: true,
            title: options.title || 'Confirm Action',
            message: options.message || 'Are you sure you want to continue?',
            confirmText: options.confirmText || 'Confirm',
            cancelText: options.cancelText || 'Cancel',
            type: options.type || 'danger',
            loading: false,
            onConfirm
        })
    }, [])

    const closeDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, isOpen: false, loading: false }))
    }, [])

    const handleConfirm = useCallback(async () => {
        try {
            setDialogState(prev => ({ ...prev, loading: true }))
            await dialogState.onConfirm()
            closeDialog()
        } catch (error) {
            console.error('Error in confirm dialog:', error)
            setDialogState(prev => ({ ...prev, loading: false }))
            // Don't close dialog on error, let user try again
        }
    }, [dialogState.onConfirm, closeDialog])

    return {
        dialogState,
        showConfirmDialog,
        closeDialog,
        handleConfirm
    }
}